import React from 'react';
import {
  Image,
  Music,
  FileCode,
  Type,
  FileJson,
  File,
  Grid3X3,
  Check,
  Loader2,
  AlertCircle,
  Trash2,
  Settings,
} from 'lucide-react';
import { ImportFileInfo, formatFileSize } from '../../../../core/types/import.types';
import { cn } from '../../../utils/cn';

interface Props {
  file: ImportFileInfo;
  isSelected: boolean;
  onSelect: () => void;
  onToggleChecked: () => void;
  onRemove: () => void;
  onConfigure: () => void;
}

const TypeIcon: React.FC<{ type: ImportFileInfo['type']; size?: number }> = ({ type, size = 20 }) => {
  switch (type) {
    case 'image':
      return <Image size={size} className="text-green-400" />;
    case 'audio':
      return <Music size={size} className="text-purple-400" />;
    case 'script':
      return <FileCode size={size} className="text-yellow-400" />;
    case 'font':
      return <Type size={size} className="text-blue-400" />;
    case 'json':
      return <FileJson size={size} className="text-orange-400" />;
    case 'spritesheet':
      return <Grid3X3 size={size} className="text-cyan-400" />;
    case 'spine':
      return <File size={size} className="text-pink-400" />;
    default:
      return <File size={size} className="text-zinc-400" />;
  }
};

const StatusIcon: React.FC<{ status: ImportFileInfo['status'] }> = ({ status }) => {
  switch (status) {
    case 'processing':
      return <Loader2 size={16} className="text-blue-400 animate-spin" />;
    case 'complete':
      return <Check size={16} className="text-green-400" />;
    case 'error':
      return <AlertCircle size={16} className="text-red-400" />;
    default:
      return null;
  }
};

export const ImportFileItem: React.FC<Props> = ({
  file,
  isSelected,
  onSelect,
  onToggleChecked,
  onRemove,
  onConfigure,
}) => {
  return (
    <div
      className={cn(
        'group flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors',
        isSelected ? 'bg-blue-600/20 border border-blue-500/50' : 'hover:bg-zinc-700/50 border border-transparent',
        file.status === 'error' && 'bg-red-900/20 border-red-500/30'
      )}
      onClick={onSelect}
    >
      {/* Checkbox */}
      <label
        className="flex items-center cursor-pointer"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={file.isSelected}
          onChange={onToggleChecked}
          disabled={file.status === 'processing' || file.status === 'complete'}
          className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
        />
      </label>

      {/* Preview / Icon */}
      <div className="flex-shrink-0 w-10 h-10 rounded overflow-hidden bg-zinc-800 flex items-center justify-center">
        {file.previewUrl ? (
          <img
            src={file.previewUrl}
            alt={file.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <TypeIcon type={file.type} />
        )}
      </div>

      {/* File info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-zinc-200 truncate">
            {file.customName || file.name}
          </span>
          {file.isSpriteSheet && (
            <span className="px-1.5 py-0.5 text-xs bg-cyan-600/30 text-cyan-400 rounded">
              Spritesheet
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span>{formatFileSize(file.size)}</span>
          {file.dimensions && (
            <>
              <span className="text-zinc-600">|</span>
              <span>{file.dimensions.width} x {file.dimensions.height}</span>
            </>
          )}
          <span className="text-zinc-600">|</span>
          <span className="capitalize">{file.type}</span>
        </div>
        {file.error && (
          <p className="text-xs text-red-400 mt-0.5 truncate">{file.error}</p>
        )}
      </div>

      {/* Progress bar (during import) */}
      {file.status === 'processing' && (
        <div className="w-20 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 transition-all duration-200"
            style={{ width: `${file.progress}%` }}
          />
        </div>
      )}

      {/* Status indicator */}
      <StatusIcon status={file.status} />

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onConfigure();
          }}
          className="p-1.5 hover:bg-zinc-600 rounded text-zinc-400 hover:text-zinc-200 transition-colors"
          title="Configure"
        >
          <Settings size={14} />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="p-1.5 hover:bg-red-600/30 rounded text-zinc-400 hover:text-red-400 transition-colors"
          title="Remove"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};
